/**
 * TOTP 입력 페이지 — challengeId 가 있는 사용자만 접근 (location.state).
 *
 * device 등급(offerTrustDevice=true)일 때 "이 기기를 90일간 신뢰" 체크박스 + 별명 입력.
 * mfa 등급(offerTrustDevice=false)이면 코드만.
 *
 * [복구 코드로 로그인] (v2.231.0 · 점검 F-11) — OTP 앱이 든 휴대폰을 잃었을 때.
 *   서버 통로(POST /auth/totp/recovery)는 처음부터 있었는데 화면에 들어갈 길이 없어,
 *   등록 때 받아 둔 복구 코드를 쓸 수 없었다. 복구 코드는 한 번 쓰면 사라진다.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { ApiCallError } from '../lib/api';
import * as authApi from '../lib/auth-api';
import { useAuth } from '../lib/auth-context';

interface TotpState {
  challengeId: string;
  offerTrustDevice: boolean;
  redirectTo: string;
}

/** 복구 코드 모양 — 「ABCD-EFGH」(대시는 있어도 없어도 된다 · 서버가 대시·대소문자를 무시한다) */
function normalizeRecoveryInput(v: string): string {
  return v.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 9);
}

export function LoginTotpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();

  const state = location.state as TotpState | null;
  const [mode, setMode] = useState<'totp' | 'recovery'>('totp');
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // 직접 URL 로 들어온 경우 — 챌린지 없으니 로그인부터
  if (!state?.challengeId) {
    return <Navigate to="/login" replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (mode === 'recovery') {
      await submitRecovery();
      return;
    }
    if (state!.offerTrustDevice && trustDevice && !deviceName.trim()) {
      setError('이 기기에 이름을 지어주세요');
      return;
    }
    setSubmitting(true);
    try {
      const { user } = await authApi.totpVerify({
        challengeId: state!.challengeId,
        code: code.trim(),
        ...(state!.offerTrustDevice && trustDevice ? { deviceName: deviceName.trim() } : {}),
      });
      setUser(user);
      navigate(state!.redirectTo || '/', { replace: true });
    } catch (e) {
      const msg = e instanceof ApiCallError ? e.message : '검증 실패';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function submitRecovery() {
    setSubmitting(true);
    try {
      const { user, recoveryCodesRemaining } = await authApi.totpRecovery({
        challengeId: state!.challengeId,
        code: recoveryCode.trim(),
      });
      alert(
        `복구 코드로 로그인했습니다. 이 코드는 다시 쓸 수 없습니다 (남은 복구 코드 ${recoveryCodesRemaining}개).\n\n` +
        `휴대폰을 잃었다면 마이페이지 → 「2단계 인증 (OTP)」에서 해제한 뒤 새 휴대폰으로 다시 등록하세요.`,
      );
      setUser(user);
      navigate(state!.redirectTo || '/', { replace: true });
    } catch (e) {
      const msg = e instanceof ApiCallError ? e.message : '검증 실패';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  function switchMode(next: 'totp' | 'recovery') {
    setMode(next);
    setError(null);
  }

  const recoveryReady = recoveryCode.replace(/-/g, '').length === 8;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm bg-white rounded shadow-sm border border-slate-200 p-8"
      >
        <div className="mb-6 text-center">
          <h1 className="text-xl font-bold">2차 인증</h1>
          <p className="mt-1 text-xs text-slate-500">
            {mode === 'totp'
              ? 'OTP 앱(Google Authenticator 등)에서 6자리 코드를 확인하세요'
              : '2단계 인증을 등록할 때 받아 둔 복구 코드(예: ABCD-EFGH) 하나를 넣으세요'}
          </p>
        </div>

        {mode === 'totp' ? (
          <label className="block mb-3">
            <span className="text-sm text-slate-700">인증 코드 (6자리)</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              autoFocus
              required
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-center text-lg tracking-widest font-mono outline-none focus:border-blue-500"
              placeholder="000000"
            />
          </label>
        ) : (
          <label className="block mb-3">
            <span className="text-sm text-slate-700">복구 코드</span>
            <input
              type="text"
              autoCapitalize="characters"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              maxLength={9}
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(normalizeRecoveryInput(e.target.value))}
              autoFocus
              required
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-center text-lg tracking-widest font-mono outline-none focus:border-blue-500"
              placeholder="ABCD-EFGH"
            />
            <span className="mt-1 block text-xs text-slate-500">
              복구 코드는 한 번 쓰면 사라집니다. 복구 코드도 없으면 시스템 관리자에게 2단계 인증 초기화를 요청하세요.
            </span>
          </label>
        )}

        {mode === 'totp' && state.offerTrustDevice && (
          <div className="mb-3 rounded border border-slate-200 bg-slate-50 p-3">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
              />
              <span>이 기기를 90일간 신뢰 (다음 로그인 시 OTP 생략)</span>
            </label>
            {trustDevice && (
              <label className="block mt-2">
                <span className="text-xs text-slate-600">기기 이름</span>
                <input
                  type="text"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  placeholder="예: 회사 노트북"
                  maxLength={100}
                  className="ui-input mt-1 w-full"
                />
              </label>
            )}
          </div>
        )}

        {error && (
          <div className="mb-3 rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || (mode === 'totp' ? code.length !== 6 : !recoveryReady)}
          className="btn btn-primary w-full"
        >
          {submitting ? '확인 중…' : '확인'}
        </button>

        <div className="mt-4 text-center">
          {mode === 'totp' ? (
            <button
              type="button"
              onClick={() => switchMode('recovery')}
              className="text-xs text-slate-500 hover:text-blue-600 hover:underline"
            >
              휴대폰을 잃었나요? 복구 코드로 로그인
            </button>
          ) : (
            <button
              type="button"
              onClick={() => switchMode('totp')}
              className="text-xs text-slate-500 hover:text-blue-600 hover:underline"
            >
              ← OTP 앱 코드로 로그인
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
